import { Injectable, OnDestroy } from '@angular/core';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { AuthTokenStorageService } from '../../../app/init/auth-token-storage.service';
import { ChatMessageDto } from '../../chats/chat.models';

export interface UnreadCountChangedEvent {
  conversaId: number;
  unreadCount: number;
}

export interface MessagesReadEvent {
  conversaId: number;
  readerUserId: number;
  readAt: string;
  messageIds?: number[];
}

const API_BASE_URL = window.location.hostname.includes('localhost')
  ? 'https://api.academy.localhost.smosc.com.br:10041'
  : 'https://api.academy.smosc.com.br';

@Injectable({ providedIn: 'root' })
export class ChatHubService implements OnDestroy {
  private connection: HubConnection | null = null;
  private startPromise: Promise<void> | null = null;

  readonly messageReceived$ = new Subject<ChatMessageDto>();
  readonly unreadCountChanged$ = new Subject<UnreadCountChangedEvent>();
  readonly conversationUpdated$ = new Subject<number>();
  readonly messagesRead$ = new Subject<MessagesReadEvent>();

  constructor(private readonly authTokenStorage: AuthTokenStorageService) {}

  connect(): Promise<void> {
    if (this.startPromise) {
      return this.startPromise;
    }

    const hub = new HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/hubs/chat`, {
        accessTokenFactory: () => this.authTokenStorage.getAccessToken(),
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    hub.on('ReceiveMessage', (message: ChatMessageDto) => {
      this.messageReceived$.next(message);
    });

    hub.on('UnreadCountChanged', (event: UnreadCountChangedEvent) => {
      this.unreadCountChanged$.next(event);
    });

    hub.on('MessagesRead', (event: MessagesReadEvent) => {
      this.messagesRead$.next(event);
    });

    hub.on('ConversationUpdated', (conversaId: number) => {
      this.conversationUpdated$.next(conversaId);
    });

    this.connection = hub;
    this.startPromise = hub.start().catch((err) => {
      this.startPromise = null;
      throw err;
    });

    return this.startPromise;
  }

  async joinConversation(conversaId: number): Promise<void> {
    await this.ensureConnected();
    await this.connection?.invoke('JoinConversation', conversaId);
  }

  async leaveConversation(conversaId: number): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) {
      await this.connection.invoke('LeaveConversation', conversaId);
    }
  }

  disconnect(): void {
    this.connection?.stop();
    this.connection = null;
    this.startPromise = null;
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private async ensureConnected(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) {
      return;
    }
    await this.connect();
  }
}
